import { Button, Grid, Tab, Tabs, Typography } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid";
import {
  ApiError,
  ApiErrorClass,
  Profile,
  TestScore as TestScoreCommon,
} from "@readyfit-common/models";
import { handleApiError } from "@readyfit-common/utils";
import { capitalize } from "lodash";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  AssignTestIcon,
  DumbbbellIcon,
  TransferIcon,
} from "src/grading/assets";
import { DisableUserButton } from "src/grading/components";
import AssignTrainingModal from "src/grading/components/AssignTrainingModal/AssignTrainingModal";
import AthleteIcon from "src/grading/components/AthleteIcon/AthleteIcon";
import ConfirmationModal from "src/grading/components/ConfirmationModal/ConfirmationModal";
import EditWorkoutModal from "src/grading/components/EditWorkoutModal/EditWorkoutModal";
import GradingHeader from "src/grading/components/GradingHeader/GradingHeader";
import AthletesLayout from "src/grading/layout/AthletesLayout/AthletesLayout";
import { DEFAULT_ORGANIZATION } from "src/grading/models";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { selectCurrentOrgID, selectCurrentUser } from "src/store/reducers/auth/authSlice";
import { useLazyExportTestScoresQuery } from "src/store/reducers/documents/documentsApiSlice";
import {
  addBreadcrumb,
  clearBreadcrumbs,
  showSnackbar,
} from "src/store/reducers/navigation/navigationSlice";
import {
  OrganizationRecruitmentProcess,
  useLazyGetOrganizationRecruitmentProcessQuery,
} from "src/store/reducers/organizations/organizationsApiSlice";
import {
  TestScore,
  useLazyGetOrganizationScoresQuery,
} from "src/store/reducers/scores/scoresApiSlice";
import {
  selectDeleteWeekModalOpen,
  selectDeleteWorkoutModalOpen,
  selectSelectedWeek,
  selectSelectedWorkout,
  selectWorkoutModalOpen,
  setDeleteWeekModalOpen,
  setDeleteWorkoutModalOpen,
  setSelectedWeek,
  setSelectedWorkout,
  useDeleteAthleteWeekMutation,
  useDeleteAthleteWorkoutMutation,
  useEndTrainingPlanMutation,
  useGetAthleteTrainingPlanQuery,
} from "src/store/reducers/training/trainingApiSlice";
import { useLazyGetUserByIdQuery } from "src/store/reducers/users/usersApiSlice";
import theme from "src/theme";
import useStyles from "./AthleteDetails.styles";
import LastTestScoreModal from "./LastTestScoreModal/LastTestScoreModal";
import ProfileCard from "./ProfileCard/ProfileCard";
import TestingTab from "./Tabs/TestingTab";
import TrainingTab from "./Tabs/TrainingTab";
import TransferAthleteModal from "./TransferAthleteModal/TransferAthleteModal";

export const formatDate = (date: string) => {
  if (!date) {
    return "";
  }
  const newDate = new Date(date);
  const formattedDate =
    (newDate.getMonth() + 1 + "").padStart(2, "0") +
    "/" +
    (newDate.getDate() + "").padStart(2, "0") +
    "/" +
    newDate.getFullYear();

  return formattedDate;
};

const AthleteDetails = () => {
  const [athlete, setAthlete] = useState<Profile | null>(null);
  const [scores, setScores] = useState<TestScoreCommon[] | TestScore[]>([]);
  const [organizationRecruitmentProcess, setOrganizationRecruitmentProcess] =
    useState<OrganizationRecruitmentProcess | null>(null);
  const { athleteId } = useParams<{ athleteId: string; }>();
  const [getUserById, { isLoading: isUserLoading }] = useLazyGetUserByIdQuery();
  const [getScores, { isFetching: areScoreLoading }] = useLazyGetOrganizationScoresQuery();
  const [getOrgRP] = useLazyGetOrganizationRecruitmentProcessQuery();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const classes = useStyles();
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "timestamp", sort: "asc" },
  ]);
  const [sortBy, setSortBy] = useState("-1");
  const lastTestScore =
    scores[
    sortBy?.toLowerCase().includes("asc") || sortBy === "-1"
      ? scores.length - 1
      : 0
    ];
  const [lastTestScoreModalOpen, setLastTestScoreModalOpen] = useState(false);
  const user = useSelector(selectCurrentUser);
  const [exportTestScores] = useLazyExportTestScoresQuery();
  const [transferAthleteModalOpen, setTransferAthleteModalOpen] =
    useState(false);
  const location = useLocation<{ tab?: number; }>();
  const tab = location.state?.tab;
  const [tabValue, setTabValue] = useState(tab || 0);
  const { data: trainingPlanData } = useGetAthleteTrainingPlanQuery(athleteId);
  const trainingPlan = trainingPlanData?.data?.plan;
  const trainingPlanMeta = trainingPlanData?.data?.meta;
  const deleteWorkoutModalOpen = useAppSelector(selectDeleteWorkoutModalOpen);
  const [deleteAthleteWorkout, { isLoading: isLoadingDeleteWorkout }] =
    useDeleteAthleteWorkoutMutation();
  const [deleteAthleteWeek, { isLoading: isLoadingDeleteWeek }] =
    useDeleteAthleteWeekMutation();
  const selectedWorkout = useAppSelector(selectSelectedWorkout);
  const selectedWeek = useAppSelector(selectSelectedWeek);
  const deleteWeekModalOpen = useAppSelector(selectDeleteWeekModalOpen);
  const workoutModalOpen = useAppSelector(selectWorkoutModalOpen);
  const [assignTrainingModalOpen, setAssignTrainingModalOpen] = useState(false);
  const [endTrainingModalOpen, setEndTrainingModalOpen] = useState(false);
  const [endTrainingPlan, { isLoading: isLoadingEndTrainingPlan }] =
    useEndTrainingPlanMutation();

  const currentOrgID = useSelector(selectCurrentOrgID);

  const getAthleteScores = async () => {
    const response = await getScores({
      organizationId: currentOrgID ?? DEFAULT_ORGANIZATION.id,
      userId: athleteId,
      ...(sortBy !== "-1" && {
        sortBy,
      }),
    }).unwrap();

    if (response.testScores) {
      setScores(response.testScores);
    }
  };

  const getOrganizationRecruitmentProcess = async () => {
    const response = await getOrgRP({
      organizationId: user.organizationId
        ? user.organizationId
        : DEFAULT_ORGANIZATION.id,
    }).unwrap();

    if (response?.recruitmentProcess) {
      setOrganizationRecruitmentProcess(response.recruitmentProcess);
    }
  };

  useEffect(() => {
    const fetchAthlete = async () => {
      try {
        const response = await getUserById(athleteId).unwrap();
        if (response?.profile) {
          setAthlete(response.profile);
        }
      } catch (error: any) {
        alert(error?.data?.message || "There was an error");
        history.goBack();
      }
    };

    dispatch(
      addBreadcrumb({
        label: "Athlete Profile",
        path: `/grading/athletes`,
      }),
    );

    Promise.all([
      fetchAthlete(),
      getAthleteScores(),
      getOrganizationRecruitmentProcess(),
    ]);

    return () => {
      dispatch(clearBreadcrumbs());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  const handleExportPastTests = async () => {
    let url = "";
    try {
      url = await exportTestScores({ userId: athleteId, sortBy }).unwrap();
    } catch (error) {
      console.log(error);
    }

    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const renderTabContent = () => {
    if (!athlete) {
      return null;
    }

    switch (tabValue) {
      case 0:
        return (
          <TrainingTab
            athlete={athlete}
            plan={trainingPlan || null}
            meta={trainingPlanMeta || null}
          />
        );
      case 1:
        return organizationRecruitmentProcess ? (
          <TestingTab
            athlete={athlete}
            organizationRecruitmentProcess={organizationRecruitmentProcess}
            sortBy={sortBy}
            setSortBy={setSortBy}
            handleExportPastTests={handleExportPastTests}
            scores={scores as TestScoreCommon[]}
            areScoreLoading={areScoreLoading}
            sortModel={sortModel}
            setSortModel={setSortModel}
            getAthleteScores={getAthleteScores}
          />
        ) : null;
      case 2:
        return <ProfileCard athlete={athlete} />;
      default:
        return null;
    }
  };

  const handleCloseDeleteWorkoutModal = () => {
    dispatch(setSelectedWorkout(null));
    dispatch(setDeleteWorkoutModalOpen(false));
  };

  const handleSubmitDeleteWorkout = async () => {
    try {
      if (!selectedWorkout?.id) {
        throw new Error("No workout id");
      }

      const response = await deleteAthleteWorkout({
        planId: trainingPlan?.id || "",
        weekId: selectedWorkout.weekId,
        workoutId: selectedWorkout.id,
      }).unwrap();

      if (!response.success) throw new Error(response.message);

      dispatch(
        showSnackbar({
          open: true,
          message: "Workout deleted successfully",
        }),
      );
    } catch (error) {
      console.log("error", error);
      dispatch(
        showSnackbar({
          open: true,
          message: "Error deleting workout",
          type: "error",
        }),
      );
    }

    handleCloseDeleteWorkoutModal();
  };
  const handleCloseDeleteWeekModal = () => {
    dispatch(setSelectedWeek(null));
    dispatch(setDeleteWeekModalOpen(false));
  };

  const handleSubmitDeleteWeek = async () => {
    try {
      if (!selectedWeek?.id) {
        throw new ApiErrorClass({
          message: "No workout id",
        });
      }

      const response = await deleteAthleteWeek({
        planId: trainingPlan?.id || "",
        weekId: selectedWeek.id,
      }).unwrap();

      if (!response.success) throw new Error(response.message);

      dispatch(
        showSnackbar({
          open: true,
          message: "Week deleted successfully",
        }),
      );
    } catch (error: unknown) {
      const typedError = error as ApiError;
      dispatch(
        showSnackbar({
          open: true,
          message: typedError.data?.message || "Error deleting week",
          type: "error",
        }),
      );
    }

    handleCloseDeleteWeekModal();
  };

  const handleCloseEndTrainingModal = () => {
    setEndTrainingModalOpen(false);
  };

  const handleSubmitEndTrainingPlan = async () => {
    try {
      if (!trainingPlan?.id) {
        throw new ApiErrorClass({
          message: "No plan id",
        });
      }

      const response = await endTrainingPlan({
        planId: trainingPlan.id,
      }).unwrap();

      if (!response.success) throw new Error(response.message);

      dispatch(
        showSnackbar({
          open: true,
          message: "Training plan ended successfully",
        }),
      );
    } catch (error: unknown) {
      handleApiError(dispatch, error as ApiError);
    }

    handleCloseEndTrainingModal();
  };

  return (
    <AthletesLayout>
      {isUserLoading ? (
        <Spinner
          animation="border"
          style={{ left: "50%", top: "50%", position: "relative" }}
        />
      ) : athlete ? (
        <Grid container>
          <GradingHeader
            // label={"Athletes / Athletes Profile"}
            onClick={() => history.push("/athletes")}
            withBreadcrumbs
          />

          <Grid container className={classes.profileIconContainer}>
            <Grid item xs={12} sm={6} md={6}>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <AthleteIcon
                    profile={athlete}
                    customStyles={classes.profileIcon}
                  />
                </Grid>
                <Grid item xs>
                  <Grid container direction="column">
                    <Typography variant="h3">
                      {athlete.firstName} {athlete.lastName}
                    </Typography>

                    <Grid container flexDirection={"row"} gap={1}>
                      <Typography variant="h5" className={classes.profileBadge}>
                        {athlete.organization?.name || "-"}
                      </Typography>

                      <Typography variant="h5" className={classes.profileBadge}>
                        {capitalize(athlete.branchOfService) || "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              className={classes.actionButtonContainer}
            >
              <Grid container flexDirection={"row"} gap={1}>
                {!trainingPlan ? (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setAssignTrainingModalOpen(true)}
                    className={classes.actionButton}
                  >
                    Assign Training
                    <DumbbbellIcon style={{ marginLeft: theme.spacing(1) }} />
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setEndTrainingModalOpen(true)}
                    className={`${classes.actionButton} ${classes.endTrainingButton}`}
                  >
                    End Training
                    <DumbbbellIcon style={{ marginLeft: theme.spacing(1) }} />
                  </Button>
                )}

                <Button
                  variant="contained"
                  size="small"
                  onClick={() => { }}
                  className={classes.actionButton}
                >
                  Assign Test
                  <AssignTestIcon style={{ marginLeft: theme.spacing(1) }} />
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setTransferAthleteModalOpen(true);
                  }}
                  className={classes.actionButton}
                >
                  Transfer Athlete
                  <TransferIcon style={{ marginLeft: theme.spacing(1) }} />
                </Button>

                <DisableUserButton athleteId={athleteId} />
              </Grid>
            </Grid>

            {/* Athlete Banner */}
            <Grid container className={classes.bannerContainer} spacing={1}>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  textTransform={"uppercase"}
                  color={"white"}
                >
                  Overview
                </Typography>
              </Grid>

              <Grid container marginTop={4} xs={12} sm={3} lg={2.4}>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    color={"#B2B8BC"}
                    textTransform={"uppercase"}
                  >
                    Primary Test
                  </Typography>
                </Grid>

                <Typography variant="h4" color={"white"}>
                  Not Assign
                </Typography>
              </Grid>

              <Grid container marginTop={4} xs={12} sm={3} lg={2.4}>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    color={"#B2B8BC"}
                    textTransform={"uppercase"}
                  >
                    Current Training Plan
                  </Typography>
                </Grid>

                <Typography variant="h4" color={"white"}>
                  {trainingPlan?.name || "Not Assign"}
                </Typography>
              </Grid>

              <Grid container marginTop={4} xs={12} sm={3} lg={2.4}>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    color={"#B2B8BC"}
                    textTransform={"uppercase"}
                  >
                    Coach
                  </Typography>
                </Grid>

                <Typography variant="h4" color={"white"}>
                  {trainingPlan?.coach?.name || "Not Assign"}
                </Typography>
              </Grid>

              <Grid container marginTop={4} xs={12} sm={3} lg={2.4}>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    color={"#B2B8BC"}
                    textTransform={"uppercase"}
                  >
                    Last Test Score
                  </Typography>
                </Grid>

                <Typography variant="h4" color={"white"}>
                  {lastTestScore?.testId
                    ? `${lastTestScore?.score} / ${lastTestScore?.maxScore}`
                    : "-"}
                </Typography>
              </Grid>

              {/* <Grid container marginTop={4} xs={12} sm={3} lg={2.4}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setLastTestScoreModalOpen(true)}
                  className={classes.seeDetailsButton}
                  disabled={!lastTestScore?.testResultId}
                >
                  See Details
                </Button>
              </Grid> */}
            </Grid>

            {/* Tabs */}
            <Grid container mt={3}>
              <Tabs
                value={tabValue}
                onChange={(event, newValue) => setTabValue(newValue)}
                className={classes.tabs}
                TabIndicatorProps={{
                  style: { display: "none" },
                }}
              >
                <Tab className={classes.tab} label="Training" />
                <Tab className={classes.tab} label="Testing" />
                <Tab className={classes.tab} label="Profile" />
              </Tabs>
            </Grid>

            {renderTabContent()}
          </Grid>
          <LastTestScoreModal
            open={lastTestScoreModalOpen}
            onClose={() => setLastTestScoreModalOpen(false)}
            lastTestScore={lastTestScore}
          />
          <TransferAthleteModal
            open={transferAthleteModalOpen}
            onClose={() => setTransferAthleteModalOpen(false)}
            athlete={athlete}
          />
          {trainingPlan && (
            <>
              {workoutModalOpen && (
                <EditWorkoutModal trainingPlan={trainingPlan} />
              )}
              <ConfirmationModal
                onClose={handleCloseDeleteWorkoutModal}
                open={deleteWorkoutModalOpen}
                isLoading={isLoadingDeleteWorkout}
                onConfirm={handleSubmitDeleteWorkout}
                title="Delete Workout"
                message="This action cannot be undone. Are you sure you want to delete the workout?"
                confirmationButtonTitle={"Delete Workout"}
              />
              <ConfirmationModal
                onClose={handleCloseDeleteWeekModal}
                open={deleteWeekModalOpen}
                isLoading={isLoadingDeleteWeek}
                onConfirm={handleSubmitDeleteWeek}
                title="Delete Week"
                message="This action cannot be undone. Are you sure you want to delete the week?"
                confirmationButtonTitle="Delete Week"
              />
              <ConfirmationModal
                onClose={handleCloseEndTrainingModal}
                open={endTrainingModalOpen}
                isLoading={isLoadingEndTrainingPlan}
                onConfirm={handleSubmitEndTrainingPlan}
                title="End Training"
                message="This action cannot be undone. Are you sure you want to end the training?"
                confirmationButtonTitle="End Training"
              />
            </>
          )}
          <AssignTrainingModal
            open={assignTrainingModalOpen}
            onClose={() => setAssignTrainingModalOpen(false)}
          />
        </Grid>
      ) : null}
    </AthletesLayout>
  );
};

export default AthleteDetails;
